import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface StepIndicatorProps {
  currentStep: number;
  steps: {
    id: number;
    name: string;
  }[];
  onStepClick?: (step: number) => void;
  className?: string;
}

/**
 * Step indicator component for multi-step forms
 * Shows the current step, completed steps, and allows navigation to previous steps
 */
export function StepIndicator({ 
  currentStep, 
  steps, 
  onStepClick, 
  className 
}: StepIndicatorProps) {
  return (
    <div className={cn("flex justify-between w-full mb-8", className)}>
      {steps.map((step) => {
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        const isClickable = isCompleted && onStepClick;
        
        return (
          <div key={step.id} className="flex flex-col items-center flex-1">
            <div className="w-full flex items-center">
              {/* Connector line before (except for first step) */}
              {step.id !== 1 && (
                <div 
                  className={cn(
                    "h-0.5 flex-1", 
                    isCompleted ? "bg-purple-500" : "bg-white/10"
                  )}
                />
              )}
              
              {/* Step circle */}
              <button
                onClick={isClickable ? () => onStepClick?.(step.id) : undefined}
                disabled={!isClickable}
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all",
                  isActive && "border-purple-500 bg-purple-500 text-white",
                  isCompleted && "border-purple-500 bg-purple-500 text-white",
                  !isActive && !isCompleted && "border-white/20 bg-white/5 text-white/50",
                  isClickable && "cursor-pointer hover:ring-2 hover:ring-purple-500/50"
                )}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span className={cn(
                    "text-sm font-medium",
                    isActive && "text-white font-bold",
                    !isActive && !isCompleted && "text-white/50"
                  )}>{step.id}</span>
                )}
              </button>
              
              {/* Connector line after (except for last step) */}
              {step.id !== steps.length && (
                <div 
                  className={cn(
                    "h-0.5 flex-1", 
                    currentStep > step.id ? "bg-purple-500" : "bg-white/10"
                  )}
                />
              )}
            </div>
            
            {/* Step label */}
            <div 
              className={cn(
                "mt-2 text-xs font-medium text-center",
                isActive ? "text-purple-500" : "text-white/60"
              )}
            >
              {step.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}
